import React, { useEffect, useState } from 'react';
import {
	getCampaignCandidates,
	getCampaigns,
	openCandidateCommunicationMethod,
} from '../../services/aiRecruiterService';
import type { Candidate, CandidateCommunicationMethod, CandidateStatus } from '../../types/aiRecruiter';
import { useVacancyStore } from '../../store/vacancyStore';
import { mapErrorCodeToLabel } from './utils/errorLabel';
import { formatCandidateShortName } from '../../relevanter/utils/candidatePrivacy';
import avitoIcon from '../../assets/icons/channels/avito.svg';
import maxIcon from '../../assets/icons/channels/max.svg';

const avitoIconSrc = typeof avitoIcon === 'string' ? avitoIcon : '';
const maxIconSrc = typeof maxIcon === 'string' ? maxIcon : '';

interface CandidatesTabProps {
	selectedCampaign: string | null;
	setSelectedCampaign: (campaignId: string | null) => void;
}

const CHANNEL_META = {
	TELEGRAM: {
		label: 'Telegram',
		className: 'text-blue-500 hover:text-blue-700',
	},
	WHATSAPP: {
		label: 'WhatsApp',
		className: 'text-green-600 hover:text-green-700',
	},
	HH: {
		label: 'HeadHunter',
		className: 'text-red-600 hover:text-red-700',
	},
	MAX: {
		label: 'Max',
		className: 'text-blue-600 hover:text-blue-700',
	},
	AVITO: {
		label: 'Авито',
		className: 'text-green-600 hover:text-green-700',
	},
} as const;

const isShortCandidateName = (value?: string | null): boolean => {
	const parts = value?.trim().split(/\s+/).filter(Boolean) || [];
	return parts.length === 2 && Array.from(parts[1]).length <= 2;
};

const normalizeCandidateIdentityText = (value?: string | null): string | null => {
	if (!value) return null;
	const normalized = value
		.normalize('NFKC')
		.replace(/[Aa]/g, 'а')
		.replace(/[Ee]/g, 'е')
		.replace(/[Oo]/g, 'о')
		.replace(/[Pp]/g, 'р')
		.replace(/[Cc]/g, 'с')
		.replace(/[Kk]/g, 'к')
		.replace(/[Xx]/g, 'х')
		.replace(/[Yy]/g, 'у')
		.replace(/\s+/g, ' ')
		.trim()
		.toLocaleLowerCase('ru-RU');
	return normalized || null;
};

const getCandidateUniqueKey = (candidate: Candidate): string => {
	const stableKey = candidate.hhResumeId
		|| candidate.candidateId
		|| candidate.sourceExternalId
		|| candidate.externalId
		|| candidate.email
		|| candidate.phone;
	const nameKey = normalizeCandidateIdentityText(
		candidate.fullName
		|| candidate.name
		|| candidate.displayName
		|| [candidate.lastName, candidate.firstName, candidate.middleName].filter(Boolean).join(' ')
	);
	return nameKey || (stableKey ? String(stableKey) : `row:${candidate.id}`);
};

const dedupeCandidatesByPerson = (candidates: Candidate[]): Candidate[] => {
	const uniqueCandidates = new Map<string, Candidate>();
	candidates.forEach((candidate) => {
		const key = getCandidateUniqueKey(candidate);
		if (!uniqueCandidates.has(key)) {
			uniqueCandidates.set(key, candidate);
		}
	});
	return Array.from(uniqueCandidates.values());
};

const getCandidateDisplayName = (candidate: Candidate): string => {
	if (candidate.pdnConsent) {
		return candidate.fullName || candidate.name || candidate.displayName || formatCandidateShortName(candidate);
	}
	const apiDisplayName = candidate.displayName || candidate.name;
	if (isShortCandidateName(apiDisplayName)) {
		return apiDisplayName!.trim();
	}
	return formatCandidateShortName(candidate, candidate.name || candidate.displayName || 'Кандидат');
};

const getCandidateCommunicationMethods = (candidate: Candidate): CandidateCommunicationMethod[] => {
	return Array.isArray(candidate.communicationMethods)
		? candidate.communicationMethods.filter((method): method is CandidateCommunicationMethod => Boolean(method?.type && method.type !== 'HH' && method.url))
		: [];
};

const getCandidateDialogueMessenger = (candidate: Candidate): keyof typeof CHANNEL_META | null => {
	const messenger = String(candidate.messengerType || '').toUpperCase();
	if (messenger === 'TELEGRAM' || messenger === 'WHATSAPP' || messenger === 'MAX' || messenger === 'HH') {
		return messenger;
	}
	return null;
};

const getStatusStyle = (status: CandidateStatus) => {
	switch (status) {
		case 'SENDING':
			return 'bg-amber-50 text-amber-700';
		case 'CONTACTED':
			return 'bg-blue-100 text-blue-800';
		case 'QUALIFIED':
			return 'bg-green-100 text-green-800';
		case 'REJECTED':
		case 'ERROR':
			return 'bg-red-100 text-red-800';
		case 'SKIPPED':
			return 'bg-gray-100 text-gray-600';
		case 'PENDING':
		default:
			return 'bg-yellow-50 text-yellow-700';
	}
};

const getStatusText = (
	status: CandidateStatus,
	skipReason?: string | null,
	errorCode?: string | null,
): string => {
	const statusMap: Record<CandidateStatus, string> = {
		PENDING: 'Ожидает',
		SENDING: 'Отправляется',
		CONTACTED: 'Контакт установлен',
		SKIPPED: 'Пропущен',
		ERROR: 'Ошибка',
		QUALIFIED: 'Одобрен',
		REJECTED: 'Отклонен',
	};

	if (status === 'SKIPPED' && skipReason) {
		const reason = skipReason.toLowerCase();
		if (reason.includes('already_applied') && !reason.includes('not_already_applied')) {
			return 'Уже откликался';
		}
	}

	if (status === 'ERROR') {
		const label = mapErrorCodeToLabel(errorCode, skipReason);
		if (label) {
			return label;
		}
	}

	return statusMap[status] || status;
};

const formatCandidateCreatedAt = (value: Candidate['createdAt']): string => {
	if (!value) return '-';
	if (Array.isArray(value)) {
		const [year, month, day, hour = 0, minute = 0] = value;
		return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
	}
	return new Date(value).toLocaleString('ru-RU');
};

const ChannelIcon = ({ type }: { type: keyof typeof CHANNEL_META }) => {
	if (type === 'TELEGRAM') {
		return (
			<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
				<path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
			</svg>
		);
	}
	if (type === 'WHATSAPP') {
		return (
			<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
				<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
			</svg>
		);
	}
	if (type === 'AVITO') {
		return avitoIconSrc
			? <img src={avitoIconSrc} alt="" className="h-4 w-4" aria-hidden="true" />
			: <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-green-600 text-[9px] font-bold text-white" aria-hidden="true">A</span>;
	}
	if (type === 'MAX') {
		return maxIconSrc
			? <img src={maxIconSrc} alt="" className="h-4 w-4" aria-hidden="true" />
			: <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-blue-600 text-[9px] font-bold text-white" aria-hidden="true">M</span>;
	}
	return (
		<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white flex-shrink-0" aria-hidden="true">
			hh
		</span>
	);
};

const CandidatesTab: React.FC<CandidatesTabProps> = ({ selectedCampaign }) => {
	const { selectedVacancyId } = useVacancyStore();
	const [candidates, setCandidates] = useState<Candidate[]>([]);
	const [loadingCandidates, setLoadingCandidates] = useState(false);

	useEffect(() => {
		let cancelled = false;

		const loadCandidatesByCurrentContext = async () => {
			if (selectedVacancyId) {
				const campaignsResponse = await getCampaigns({ positionId: Number(selectedVacancyId), size: 1000 });
				const positionCampaigns = campaignsResponse.content || [];
				const candidatesByCampaign = await Promise.all(
					positionCampaigns.map((campaign) => getCampaignCandidates(campaign.id))
				);
				return dedupeCandidatesByPerson(candidatesByCampaign.flat());
			}
			return selectedCampaign ? getCampaignCandidates(selectedCampaign) : [];
		};

		const loadCandidatesData = async () => {
			setLoadingCandidates(true);
			try {
				const candidatesList = await loadCandidatesByCurrentContext();
				if (!cancelled) setCandidates(candidatesList);
			} catch (error) {
				console.error('Failed to load candidates:', error);
				if (!cancelled) setCandidates([]);
			} finally {
				if (!cancelled) setLoadingCandidates(false);
			}
		};

		loadCandidatesData();
		return () => { cancelled = true; };
	}, [selectedCampaign, selectedVacancyId]);

	const handleOpenCommunicationMethod = async (method: CandidateCommunicationMethod) => {
		try {
			const targetUrl = await openCandidateCommunicationMethod(method.url);
			window.open(targetUrl, '_blank', 'noopener,noreferrer');
		} catch (error) {
			console.error('Failed to open candidate communication method:', error);
		}
	};

	return (
		<div className="h-full overflow-y-auto bg-[#f5f6f1] p-6">
			<div className="bg-white rounded-lg p-6 shadow-sm space-y-4">
				<div className="flex items-center justify-between gap-4">
					<h2 className="text-lg font-semibold text-gray-900">Список кандидатов</h2>
					<span className="text-sm text-gray-500">{candidates.length}</span>
				</div>

				{loadingCandidates ? (
					<div className="flex items-center justify-center py-8">
						<div className="text-gray-500">Загрузка кандидатов...</div>
					</div>
				) : candidates.length === 0 ? (
					<div className="flex items-center justify-center py-8">
						<p className="text-gray-500">Нет кандидатов по выбранной вакансии</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Имя</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Способы связи</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Мессенджер диалога</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ПДН согласие</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Резюме</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дата добавления</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{candidates.map((candidate) => {
									const candidateName = getCandidateDisplayName(candidate);
									const communicationMethods = getCandidateCommunicationMethods(candidate);
									const dialogueMessenger = getCandidateDialogueMessenger(candidate);
									const dialogueId = candidate.dialogueId || candidate.conversationId;

									return (
										<tr key={`${candidate.campaignId || 'position'}-${candidate.id}`}>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="flex items-center gap-2">
													{candidate.pdnConsent === true && candidate.photoUrl ? (
														<img
															src={candidate.photoUrl}
															alt={candidateName}
															className="w-6 h-6 rounded-full object-cover bg-gray-200"
															onError={(e) => {
																e.currentTarget.onerror = null;
																e.currentTarget.style.display = 'none';
																e.currentTarget.nextElementSibling?.classList.remove('hidden');
															}}
														/>
													) : null}
													<div className={`w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center ${candidate.pdnConsent === true && candidate.photoUrl ? 'hidden' : ''}`}>
														<svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
															<path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
														</svg>
													</div>
													<span className="text-sm font-medium text-gray-900">{candidateName}</span>
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm">
												<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusStyle(candidate.status)}`}>
													{getStatusText(candidate.status, candidate.skipReason, candidate.errorCode)}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm">
												{communicationMethods.length > 0 ? (
													<div className="flex items-center gap-2">
														{communicationMethods.map((method) => {
															const meta = CHANNEL_META[method.type];
															if (!meta) return null;
															return (
																<button
																	key={`${method.type}-${method.url}`}
																	type="button"
																	onClick={() => handleOpenCommunicationMethod(method)}
																	aria-label={meta.label}
																	title={meta.label}
																	className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200 transition-colors ${meta.className}`}
																>
																	<ChannelIcon type={method.type} />
																</button>
															);
														})}
													</div>
												) : (
													<span className="text-gray-400">-</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm">
												{dialogueMessenger === 'HH' && candidate.resumeUrl ? (
													<a
														href={candidate.resumeUrl}
														target="_blank"
														rel="noopener noreferrer"
														aria-label="HeadHunter"
														title="HeadHunter"
														className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200 transition-colors ${CHANNEL_META.HH.className}`}
													>
														<ChannelIcon type="HH" />
													</a>
												) : dialogueMessenger && dialogueMessenger !== 'HH' && dialogueId ? (
													<button
														type="button"
														onClick={() => window.open(`/recruiter/messenger?dialogId=${encodeURIComponent(String(dialogueId))}`, '_blank', 'noopener,noreferrer')}
														aria-label={CHANNEL_META[dialogueMessenger].label}
														title={CHANNEL_META[dialogueMessenger].label}
														className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200 transition-colors ${CHANNEL_META[dialogueMessenger].className}`}
													>
														<ChannelIcon type={dialogueMessenger} />
													</button>
												) : (
													<span className="text-gray-400">-</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm">
												<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${candidate.pdnConsent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
													{candidate.pdnConsent ? 'Да' : 'Нет'}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm">
												{candidate.resumeUrl ? (
													<a
														href={candidate.resumeUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-full transition-colors"
													>
														hh.ru
													</a>
												) : (
													<span className="text-gray-400">-</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
												{formatCandidateCreatedAt(candidate.createdAt)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
};

export default CandidatesTab;
